/*
  The set's most graded cards, as a shelf of tiles above the table: the shape the game page
  uses for its top sets (a short ranked shelf first, then everything), with each card's art
  where the game page has a set logo.

  Six, so the shelf fills its rows at two, three and six across. Each tile carries the
  card's rank, number and rarity, its graded count and how many of those were 10s. Cards
  never graded are not ranked, and with fewer than two graded cards there is nothing worth
  a shelf, so it is absent.
*/

import { Link } from 'react-router-dom';

import { CARD_TITLE_LINK, TILE, TILE_CHIP, TILE_COUNT, TILE_COUNT_FIGURE, TILE_COUNT_LABEL } from 'components/styles';
import { compareCardNumbers, formatCount, formatShare, tallyGradeCounts, tallyTens, tallyTotal } from 'lib';
import { sanitizeURIPathSegment, sanitizeURISlug } from 'helpers/routing';
import type { CardInfo } from 'types/gameInfo';

const SHELF_SIZE = 6;

interface SetTopCardsProps {
  gameCode: string;
  setName: string;
  /* Undefined while the set's cards are loading: the shelf holds its place with skeletons. */
  cards: CardInfo[] | undefined;
}

const HEADING_ID = 'top-cards-heading';

const ART_PANEL =
  'relative flex h-[170px] items-center sm:h-[210px] justify-center border-b border-pcg-border bg-pcg-raised/50 p-3';

const SHELF_GRID = 'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6';

const ShelfHeading = () => (
  <div className="mb-4 flex items-center gap-3">
    <h2 id={HEADING_ID} className="font-display text-lg font-semibold text-pcg-text sm:text-xl">
      Most Graded Cards
    </h2>
    <span aria-hidden="true" className="h-px flex-1 bg-pcg-goldMuted" />
    <span className="shrink-0 text-xs uppercase tracking-wide text-pcg-textMuted">Ranked by cards graded</span>
  </div>
);

const SetTopCards = ({ gameCode, setName, cards }: SetTopCardsProps) => {
  /*
    While the cards load, the shelf is drawn as skeleton tiles the shape of the real ones.
    It used to be absent until the cards arrived and then appear above "All Cards", pushing
    the whole page down by its height: a 0.19 layout shift on every set page. A set with
    fewer than two graded cards still loses the shelf when they land, which is rare.
  */
  if (!cards) {
    return (
      <section aria-labelledby={HEADING_ID} aria-busy="true">
        <ShelfHeading />
        <ol aria-hidden="true" className={SHELF_GRID}>
          {Array.from({ length: SHELF_SIZE }).map((_unused, index) => (
            <li key={`skeleton-${index}`} className={TILE}>
              <span className={`${ART_PANEL} animate-pulse`} />
              <span className="flex flex-col gap-1.5 px-3 py-2.5">
                <span className="block h-[35px] w-3/4 animate-pulse rounded bg-pcg-raised" />
                <span className="block h-4 w-1/2 animate-pulse rounded bg-pcg-raised" />
                <span className="block h-5 w-1/3 animate-pulse rounded bg-pcg-raised" />
              </span>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  const top = cards
    .filter(card => (card.cardsGraded ?? 0) > 0)
    .sort(
      (a, b) => (b.cardsGraded ?? 0) - (a.cardsGraded ?? 0) || compareCardNumbers(a.number, b.number)
    )
    .slice(0, SHELF_SIZE);

  if (top.length < 2) {
    return null;
  }

  return (
    <section aria-labelledby={HEADING_ID}>
      <ShelfHeading />
      <ol className={SHELF_GRID}>
        {top.map((card, index) => {
          const to = `/${gameCode}/${sanitizeURIPathSegment(setName)}/${sanitizeURISlug(card.number)}`;
          const tally = tallyGradeCounts([card]);
          const total = tallyTotal(tally);
          const tens = tallyTens(tally);
          return (
            <li key={card.id} className={TILE}>
              <Link
                to={to}
                tabIndex={-1}
                aria-hidden="true"
                className={ART_PANEL}
              >
                {card.image ? (
                  <img
                    src={card.image}
                    alt=""
                    loading="lazy"
                    className="max-h-full max-w-full rounded-md object-contain shadow-md transition-transform group-hover:scale-[1.03]"
                  />
                ) : null}
                <span className={`absolute left-2 top-2 ${TILE_CHIP} text-[10px]`}>#{index + 1}</span>
              </Link>
              <div className="flex flex-1 flex-col gap-1.5 px-3 py-2.5">
                <h3 className="skin-tile-title text-sm leading-tight">
                  <Link to={to} title={card.title} className={`line-clamp-2 ${CARD_TITLE_LINK}`}>
                    {card.title}
                  </Link>
                </h3>
                <p className="truncate text-xs text-pcg-textMuted">
                  #{card.number}
                  {card.rarity ? ` · ${card.rarity}` : ''}
                </p>
                <p className={`${TILE_COUNT} flex-wrap justify-between gap-x-2`}>
                  <span className="whitespace-nowrap">
                    <span className={TILE_COUNT_FIGURE}>{formatCount(card.cardsGraded ?? 0)}</span>{' '}
                    <span className={TILE_COUNT_LABEL}>graded</span>
                  </span>
                  {tens > 0 && total > 0 ? (
                    <span className="whitespace-nowrap text-[11px] font-semibold text-pcg-textMuted">
                      10s {formatShare((tens / total) * 100)}
                    </span>
                  ) : null}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default SetTopCards;
